import * as DOMUtils from "history/DOMUtils";
import type { UscVehicleReducerType } from "../redux/reducers/UscVehicleReducer";
import type { DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import {
    type UscVehicleType,
    doesDealerOfferCarDelivery,
    getVehicleSaleType,
    vehicleHasValidProgram,
    vehicleHasWarranty,
} from "../../shared-logic/utils/uscUtils";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";
import {
    type EquipmentType,
    type ProductVersionIdentifierType,
    type UscCustomerPromiseBannerType,
    type VehicleType,
    UscContext,
    UscVehicleSaleType,
} from "../../shared-logic/types/UscCommonTypes";
import { type UscLabelType, getUscLabel } from "./labels";
import { type ScrollTargetSelector, DealerInfoType } from "./constants";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import { isRetailer, objectToQueryString, queryStringToObject } from "../../../common-deprecated/utils";
import {
    type DfProductVersionIdentifier,
    DfEquipmentCategoryCode,
    DfVehicleStatus,
} from "../../shared-logic/utils/dfConstants";
import type { FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import { postalPublish } from "../../../common-deprecated/utils/postalUtils";
import {
    POSTAL_CHANNEL_T1_CARCONFIG,
    POSTAL_TOPIC_STOCK_CARS_BUY,
    POSTAL_TOPIC_USED_CARS_BUY,
} from "../../../common-deprecated/constants";
import type { ToolsModalItemType } from "../../../common-deprecated/features/new-cars/debug-modal/components/ToolsModal";

export type DealerInfoItemType = { type: DealerInfoType; text: string; link: string };

export const getDealerContactInfoList = (
    dealer: DealerResultType,
    uscSettings: UscSettingsType,
    commonSettings: CommonSettingsType,
): DealerInfoItemType[] => {
    const list: DealerInfoItemType[] = [];

    if (uscSettings.showDealerPhoneNumber && dealer.phoneNumber) {
        list.push({
            type: DealerInfoType.PhoneNumber,
            text: dealer.phoneNumber,
            link: `tel:${dealer.phoneNumber.replace(/\s*\.*\/*/g, "")}`,
        });
    }

    if (uscSettings.showDealerEmail && dealer.email) {
        list.push({
            type: DealerInfoType.Email,
            text: dealer.email,
            link: `mailto:${dealer.email}`,
        });
    }

    if (uscSettings.showDealerWebsite && dealer.website) {
        list.push({
            type: DealerInfoType.Website,
            text: dealer.website.replace(/https?:\/\//, ""), // hide protocol from url to decrease length
            link: dealer.website,
        });
    }

    // The impressum is not shown on retailer screens
    if (uscSettings.showDealerImpressum && dealer.impressum && !isRetailer(commonSettings)) {
        list.push({
            type: DealerInfoType.Impressum,
            text: getUscLabel({ uscSettings, commonSettings }, "uscPdpDealerImpressumTitle"),
            link: dealer.impressum,
        });
    }

    return list;
};

/**
 * "Reserve and buy car" CTA logic
 */
export const openCheckout = (
    vehicleForSaleId: string,
    tyCode: string,
    financeOption: FinanceOptionType,
    context: UscContext,
    isReserve: boolean,
): void => {
    const isUsed = context === UscContext.Used;
    postalPublish({
        channel: POSTAL_CHANNEL_T1_CARCONFIG,
        topic: isUsed ? POSTAL_TOPIC_USED_CARS_BUY : POSTAL_TOPIC_STOCK_CARS_BUY,
        data: {
            personalCode: tyCode,
            carType: isUsed ? "usedCars" : "stockCars",
            financeOption,
            [isUsed ? "usedCarId" : "stockCarId"]: vehicleForSaleId,
            type: isReserve ? "reserve" : "buy",
        },
    });
};

export const getLinkToLocalReservation = (vehicle: VehicleType, localOnlineReservationUrl: string): string => {
    const queryString = new URLSearchParams({
        vehicleForSaleId: vehicle.id,
        price: vehicle.price?.sellingPriceInclVAT?.toString() || "",
        imageUrl: vehicle.images[0]?.url || "",
    }).toString();

    return `${localOnlineReservationUrl}?${queryString}`;
};

// mashup of equipment and spec category iconTypes
export type AccordionIconType =
    | "dimensions" // default
    | "consumption"
    | "engine"
    | "performance"
    | "transmission"
    | "comfort"
    | "exterior"
    | "interior"
    | "multimedia"
    | "security"
    | "styling"
    | "tyreInfo";

type EquipmentCategoryType = {
    code: DfEquipmentCategoryCode;
    title: string;
    iconType: AccordionIconType;
    equipment: string[];
};

/**
 * Groups a flat list of equipment into groups based on equipmentType and adds a matching iconType
 */
export const groupEquipmentIntoCategories = (equipment: EquipmentType[]): EquipmentCategoryType[] => {
    const equipmentCatIconMap: Partial<Record<DfEquipmentCategoryCode, AccordionIconType>> = {
        [DfEquipmentCategoryCode.Comfort]: "comfort",
        [DfEquipmentCategoryCode.Exterior]: "exterior",
        [DfEquipmentCategoryCode.Seats]: "interior",
        [DfEquipmentCategoryCode.Interior]: "interior",
        [DfEquipmentCategoryCode.Safety]: "security",
        [DfEquipmentCategoryCode.Multimedia]: "multimedia",
        [DfEquipmentCategoryCode.Options]: "styling",
        [DfEquipmentCategoryCode.Others]: "dimensions",
    };

    const hasEquipment = equipment.length > 0;
    if (!hasEquipment) return [];

    const equipmentMap = equipment.reduce<{ [key in DfEquipmentCategoryCode]: EquipmentCategoryType }>(
        (all, item) => {
            const code = item.type?.code || DfEquipmentCategoryCode.Others;
            // make new category
            if (!all[code]) {
                all[code] = {
                    code,
                    equipment: [],
                    title: item.type?.description || "",
                    iconType: equipmentCatIconMap[code] || "dimensions",
                };
            }

            all[code].equipment.push(item.name);

            return all;
        },
        {} as { [key in DfEquipmentCategoryCode]: EquipmentCategoryType },
    );

    return Object.values(equipmentMap);
};
/**
 *
 * returns the correct BasketDisclaimerLabel based on vehicleType and financeOption
 */
export const getBasketDisclaimerLabel = (financeOption: FinanceOptionType, vehicleType: UscContext): UscLabelType => {
    if (vehicleType === UscContext.Used) {
        if (financeOption === "cash") return "uscBasketDisclaimerUsedCash";
        if (financeOption === "monthly") return "uscBasketDisclaimerUsedMonthly";
    }
    if (vehicleType === UscContext.Stock) {
        if (financeOption === "cash") return "uscBasketDisclaimerStockCash";
        if (financeOption === "monthly") return "uscBasketDisclaimerStockMonthly";
    }
    throw new Error("Cannot find correct disclaimer label key for current vehicle type and finance option");
};

/**
 * Maps the values from the query to the DF values
 */
export const vehicleStatusQueryMap: Record<string, DfVehicleStatus> = {
    available: DfVehicleStatus.Available,
    reserved: DfVehicleStatus.Reserved,
    sold: DfVehicleStatus.Sold,
};

export const getUscToolsModalItems = (): ToolsModalItemType[] => {
    if (!DOMUtils.canUseDOM) return [];

    const { location } = window;
    const currentPath = `${location.origin}${location.pathname}`;
    const parsedQuery = queryStringToObject(location.search);

    const debugLinks = {
        title: "Vehicle status overrides",
        links: Object.keys(vehicleStatusQueryMap).map((status) => ({
            title: status,
            href: `${currentPath}?${objectToQueryString({ ...parsedQuery, vehicleStatus: status })}`,
        })),
    };

    return [debugLinks];
};

/**
 * Creates a unique name for a vehicle (without dealer info).
 * Used for Image alt tags in Carousel and in tracking.
 *
 * Make sure the trackingName property in car-filter matches this string as well (used as the product.productName prop).
 */
export const getVehicleFullName = (vehicleResult: UscVehicleType): string => {
    const { product } = vehicleResult;
    const { modelYear, brand, model, versionName } = product;
    // This string used to contain exteriorColour, however this string should be synced between car-filter and USC.
    // The exteriorColour string is only available in the vehicle gateway which is not used at the car-filter side.
    return `${modelYear} ${brand.description} ${model} - ${versionName}`;
};

/**
 * Returns a product version iIdentifier based on a version type code
 */
export const findProductVersion = (
    vehicleResult: VehicleType,
    versionTypeCode: DfProductVersionIdentifier,
): ProductVersionIdentifierType | undefined => {
    return vehicleResult.product.productVersionIdentifiers.find(
        (version) => version.versionType.code === versionTypeCode,
    );
};

export enum UscService {
    Warranty = "warranty",
    CarDelivery = "carDelivery",
}

/**
 * Returns a list of all active services.
 * ⚠️ This does not include finance and insurance as these are rendered separately.
 * See component 'ServicesWithDetailsList' and util 'getEnabledServicesWithDetails'
 */
export const getEnabledServices = (
    vehicle: UscVehicleReducerType,
    settings: UscSettingsType,
    dealer: DealerResultType | null,
): UscService[] => {
    const list: UscService[] = [];

    if (!vehicle.result) return list;

    if (settings.enableCarDelivery && doesDealerOfferCarDelivery(dealer)) list.push(UscService.CarDelivery);
    if (vehicleHasWarranty(vehicle.result)) list.push(UscService.Warranty);

    return list;
};

/**
 * Returns the active scroll target that is currently in view
 */
export const getActiveScrollTarget = (
    selectors: ScrollTargetSelector[],
    offset: number,
): ScrollTargetSelector | undefined => {
    // create an array with top offsets for each scroll target
    const allElements = Array.from(document.querySelectorAll("[id^='usc-section-']"));
    const mappedElements = allElements.map((element) => ({
        selector: element.id as ScrollTargetSelector,
        top: element.getBoundingClientRect().top,
    }));

    // filter out elements that are not part of the currently enabled selectors
    const enabledElements = mappedElements.filter((element) => selectors.includes(element.selector));

    // only keep passed elements
    const passedElements = enabledElements.filter((element) => element.top <= offset);

    if (!passedElements.length) return;

    const { selector } = passedElements[passedElements.length - 1];

    return selector;
};

/**
 * Returns the labels to use in the warranty banner
 */
export const getWarrantyBannerDescriptionItems = (
    settings: UscSettingsType,
    vehicle: VehicleType | null,
    brand: "toyota" | "lexus",
    context: UscContext,
    commonSettings: CommonSettingsType,
): UscCustomerPromiseBannerType | null => {
    if (!vehicle) return null;

    const { vehicleSaleType } = getVehicleSaleType(
        vehicle,
        brand,
        settings.enablePrivatelySoldVehicles,
        commonSettings,
    );

    // If the vehicle is a pledged sale, regardless of the ucProgram, we return the
    // privatelySoldBannerDescriptionItems.
    if (vehicleSaleType === UscVehicleSaleType.Private) {
        return settings.privatelySoldCustomerPromiseBanner;
    }

    // Stock cars don't have warranty. To allow NMSCs of SCLs to use the banner for other purposes
    // we show the nonUcProgramBannerDescriptionItems. This is documented in AEM.
    if (context === UscContext.Stock) {
        return settings.nonUcProgramCustomerPromiseBanner;
    } else if (!vehicleHasWarranty(vehicle)) {
        return null;
    }

    return vehicleHasValidProgram(vehicle, brand)
        ? settings.ucProgramCustomerPromiseBanner
        : settings.nonUcProgramCustomerPromiseBanner;
};

/**
 * Simple util function to determine if there are Customer Promise banner items to show
 */
export const hasCustomerPromiseBannerItems = (
    settings: UscSettingsType,
    vehicle: VehicleType | null,
    brand: "toyota" | "lexus",
    context: UscContext,
    commonSettings: CommonSettingsType,
): boolean => {
    const customerPromiseBanner = getWarrantyBannerDescriptionItems(settings, vehicle, brand, context, commonSettings);

    return !!customerPromiseBanner?.bannerItems?.length;
};
